import { useState } from 'react';
import { Button, Dialog, DialogActions, DialogBody, DialogContent, DialogSurface, DialogTitle, Field, Input, Textarea } from '@fluentui/react-components';
import type { Project } from '@qcbot/shared';
import { createProject } from '../api';

const MAX_NAME_LENGTH = 100;

/** Quick-onboarding project creation, directly from the task pane (page 1 of starting a run) —
 * so a brand-new user never has to leave PowerPoint and go to the admin panel just to get a
 * project to attach their first QC template to. Deliberately minimal: name + description only,
 * same as the admin app's CreateProjectDialog — collaborators/advanced settings stay admin-only. */
export function NewProjectDialog({ open, onClose, onCreated }: { open: boolean; onClose: () => void; onCreated: (project: Project) => void }) {
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  function handleClose() {
    setName('');
    setDescription('');
    setError(null);
    onClose();
  }

  async function handleCreate() {
    const trimmed = name.trim();
    if (!trimmed) return;
    setSubmitting(true);
    setError(null);
    try {
      const project = await createProject({ name: trimmed, description: description.trim() || undefined });
      handleClose();
      onCreated(project);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to create project');
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <Dialog open={open} onOpenChange={(_, data) => !data.open && handleClose()}>
      <DialogSurface style={{ margin: 16 }}>
        <DialogBody>
          <DialogTitle>New project</DialogTitle>
          <DialogContent>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              <Field label="Name" required>
                <Input value={name} maxLength={MAX_NAME_LENGTH} onChange={(_, d) => setName(d.value)} placeholder="e.g. Diabetes Market Research" />
              </Field>
              <Field label="Description (optional)">
                <Textarea rows={3} value={description} onChange={(_, d) => setDescription(d.value)} />
              </Field>
              {error && <div style={{ color: '#b91c1c', fontSize: 13 }}>{error}</div>}
            </div>
          </DialogContent>
          <DialogActions>
            <Button appearance="secondary" disabled={submitting} onClick={handleClose}>
              Cancel
            </Button>
            <Button appearance="primary" disabled={!name.trim() || submitting} onClick={() => void handleCreate()}>
              {submitting ? 'Creating…' : 'Create project'}
            </Button>
          </DialogActions>
        </DialogBody>
      </DialogSurface>
    </Dialog>
  );
}
